'use client';

import { datadogRum } from '@datadog/browser-rum';
import { useIdentityContext } from '@repo/platform-analytics/IdentityContext';
import { ApprovalOddsErrorCategory } from '@repo/utils/errors/approvalOddsErrors';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import * as v from 'valibot';
import { APPROVAL_ODDS_API_PATH } from '@/app/api/credit-cards/cardfinder/approval-odds/config';
import type { ApprovalOddsResponse } from './constants';
const ApprovalOddsResponseSchema = v.record(v.string(), v.object({
  id: v.string(),
  approvalLikelihood: v.string(),
  score: v.number()
}));
type ApprovalLikelihoodContextValue = {
  isLoading: boolean;
  approvalLikelihoods: ApprovalOddsResponse;
};
const ApprovalLikelihoodContext = createContext<ApprovalLikelihoodContextValue>({
  isLoading: false,
  approvalLikelihoods: {}
});
export function ApprovalLikelihoodProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isLoggedIn
  } = useIdentityContext();
  const [isLoading, setIsLoading] = useState(false);
  const [approvalLikelihoods, setApprovalLikelihoods] = useState<ApprovalOddsResponse>({});
  useEffect(() => {
    if (isLoggedIn) {
      const fetchApprovalLikelihoods = async () => {
        try {
          setIsLoading(true);
          datadogRum.addAction('Approval odds fetch started', {
            approvalOddsUrl: APPROVAL_ODDS_API_PATH
          });
          const rawData = await fetch(APPROVAL_ODDS_API_PATH, {
            credentials: 'include'
          });
          if (!rawData.ok) {
            throw new Error(`Approval odds API returned status: ${rawData.status}`);
          }
          if (rawData.status === 204) {
            // No content is an expected response for users without a TU report.
            setApprovalLikelihoods({});
            return;
          }
          const parsed = v.safeParse(ApprovalOddsResponseSchema, await rawData.json());
          if (!parsed.success) {
            throw new Error('Approval odds API returned unexpected shape');
          }
          setApprovalLikelihoods(parsed.output);
        } catch (err) {
          const error = new Error('Failed to fetch approval odds data', {
            cause: err
          });
          datadogRum.addError(error, {
            approvalOddsUrl: APPROVAL_ODDS_API_PATH,
            isLoggedIn: String(isLoggedIn),
            errorCategory: ApprovalOddsErrorCategory.FETCH_ERROR
          });
        } finally {
          setIsLoading(false);
        }
      };
      void fetchApprovalLikelihoods();
    }
  }, [isLoggedIn]);
  const value = useMemo(() => ({
    isLoading,
    approvalLikelihoods
  }), [isLoading, approvalLikelihoods]);
  return <ApprovalLikelihoodContext.Provider value={value}>
      {children}
    </ApprovalLikelihoodContext.Provider>;
}
export const useApprovalLikelihoodContext = () => {
  return useContext(ApprovalLikelihoodContext);
};